/**
 * Mobile Token Exchange page.
 *
 * This page is the return_to target after mobile login.
 * It exchanges the session cookie for a mobile token and redirects
 * to the mobile callback URL with the token.
 *
 * Flow:
 * 1. Mobile opens /login?mobile=true&callback=...
 * 2. Login flow uses return_to=/mobile-token-exchange?callback=...
 * 3. After successful login, Kratos redirects here
 * 4. This page calls /v1/auth/mobile-token
 * 5. Redirects to callback with token
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

import { config } from '../config';

// Gateway URL for mobile token exchange
const GATEWAY_URL = config.gatewayUrl;

/**
 * Validate callback is a safe redirect target.
 * Allows custom app schemes (e.g. orbitalpay://) and known base domains.
 * Rejects arbitrary web URLs to prevent open-redirect token theft.
 */
function isValidCallback(callback: string): boolean {
  // Custom schemes (mobile app deep links) are always allowed
  if (!callback.startsWith('http://') && !callback.startsWith('https://')) {
    return true;
  }
  try {
    const url = new URL(callback);
    const baseDomain = config.baseDomain;
    // Allow same hostname or *.baseDomain subdomains
    return (
      url.hostname === `passport.${baseDomain}` ||
      url.hostname.endsWith(`.${baseDomain}`) ||
      url.hostname === baseDomain
    );
  } catch {
    return false;
  }
}

export default function MobileTokenExchange() {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const callback = params.get('callback');

    if (!callback) {
      setError('Missing callback URL');
      return;
    }

    if (!isValidCallback(callback)) {
      setError('Invalid callback URL');
      return;
    }

    // Fetch mobile token
    fetchMobileToken(callback);
  }, [location.search]);

  const fetchMobileToken = async (callback: string) => {
    try {
      const response = await fetch(`${GATEWAY_URL}/v1/auth/mobile-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include session cookie
        body: JSON.stringify({
          device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Device' : 'Unknown Device',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get mobile token');
      }

      const data = await response.json();

      // Build callback URL with token and user info
      const callbackUrl = new URL(callback);
      callbackUrl.searchParams.set('token', data.token);

      // Encode user info as base64 JSON for safe URL transport
      if (data.user) {
        const userJson = JSON.stringify(data.user);
        callbackUrl.searchParams.set('user', btoa(userJson));
      }

      // Redirect to callback
      window.location.href = callbackUrl.toString();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate mobile token');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-content-primary mb-2">Authentication Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <a href="/login" className="btn-primary inline-block">
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner text="Completing login..." />
    </div>
  );
}
